import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { NotesDeluxeSection } from "../notes/notesDeluxeSection";
import { EmployeeSection } from "./EmployeeSection";
import { CustomerSection } from "./CustomerSection";
import { ContactSection } from "./ContactSection";
import { ProjectAccessesSection } from "./ProjectAccessesSection";
import { ProjectContactsSection } from "./ProjectContactsSectionV2";
import { useProjectPermissions, useUserHasAccess } from "../../../routes/roleOnly";
import axios from "../../../api/axios";
import { format, startOfWeek, endOfWeek, addWeeks } from "date-fns";

export const ProjectInfo = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh } = props;

  const projectPermissions = useProjectPermissions(item);
  const canAccessProjectPlan = useUserHasAccess({ roles: ["projects_plan"] });

  // Calculate total hours from time entries (assuming time_entries is available in item)
  const totalHours =
    item?.time_entries?.reduce((sum, entry) => sum + (entry.hours || 0), 0) ||
    0;

  // Get budget hours from project data
  const budgetHours = item?.budget_hours || 0;
  const progressPercentage =
    budgetHours > 0 ? Math.min((totalHours / budgetHours) * 100, 100) : 0;

  return (
    <>

                    {/* Project Details Section */}
                    <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{t("project.details")}</h3>
                <p className="text-sm text-gray-600">Project information and specifications</p>
              </div>
            </div>
            <div className="flex space-x-2">
              {projectPermissions.canEdit ? (
              <Link 
                to={`/dashboard/projects/${item?.id}/edit`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
{t("project.edit")}
              </Link>
              ) : null}
              {projectPermissions.canEdit ? (
              <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-slate-600 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </button>
              ) : null}
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          {/* Project Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{item?.name || "Untitled Project"}</h1>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    {item?.project_number || "No number"}
                  </span>
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    {item?.project_category?.displayTitle || "No category"}
                  </span>
                </div>
              </div>
              <div className="flex space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-500">Status</div>
                  <div className="flex items-center mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      item?.active 
                        ? item?.finished 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item?.finished ? 'Completed' : item?.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Project Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Department */}
            <div className="space-y-2">
              <div className="flex items-center text-sm font-medium text-gray-500">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Department
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {item?.department?.displayTitle || "-"}
              </div>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <div className="flex items-center text-sm font-medium text-gray-500">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Start Date
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {item?.start_date ? new Date(item.start_date).toLocaleDateString() : "-"}
              </div>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <div className="flex items-center text-sm font-medium text-gray-500">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                End Date
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {item?.end_date ? new Date(item.end_date).toLocaleDateString() : "-"}
              </div>
            </div>
          </div>

          {/* Description */}
          {item?.description && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center text-sm font-medium text-gray-500 mb-4">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Description
              </div>
              <div className="prose prose-sm max-w-none text-gray-700">
                <div dangerouslySetInnerHTML={{ __html: item.description }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Project Plan Preview Section */}
      {canAccessProjectPlan ? (
        <ProjectPlanPreview projectId={item?.id} className="mt-5" />
      ) : null}

      {!projectPermissions.isExecutorParticipant ? (
      <div className="shadow sm:overflow-hidden sm:rounded-md mt-5">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">

          {/* Progress Bar Section */}
          <div className="">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Hours Progress
              </span>
              <span className="text-sm text-gray-500">
                {totalHours.toFixed(1)} {budgetHours > 0 ? `/ ${budgetHours}` : ''} hours
              </span>
            </div>
            {budgetHours > 0 ? (
              <>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      progressPercentage >= 100
                        ? "bg-red-500"
                        : progressPercentage >= 80
                        ? "bg-yellow-500"
                        : "bg-blue-500"
                    }`}
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0h</span>
                  <span>{budgetHours}h</span>
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500">
                No budget hours set
              </div>
            )}
          </div>
        </div>
      </div>
      ) : null}

      {/* Customer Section */}
      <CustomerSection customerId={item?.customer?.id} className="mt-5" />

      {/* Delivery Address Section */}
      {item?.delivery_address && (
        <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Delivery Address</h3>
                  <p className="text-sm text-gray-600">Project delivery location</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  View on Map
                </button>
                {projectPermissions.canEdit ? (
                <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Edit Address
                </button>
                ) : null}
              </div>
            </div>
          </div>
          <div className="bg-white px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Address</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.delivery_address.displayTitle || item.delivery_address.address || "-"}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">City</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.delivery_address.city || "-"}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Postal Code</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.delivery_address.postal_code || "-"}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Country</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.delivery_address.country || "-"}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Contact Person</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.delivery_address.contact_person || "-"}</dd>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <ContactSection contactId={item?.contact?.id} className="mt-5" />

      {/* Project Manager Section */}
      <EmployeeSection 
        employeeId={item?.project_manager?.id} 
        title={t("project.manager")}
        className="mt-5" 
      />

      <ProjectContactsSection
        project={item}
        onRefresh={onRefresh}
        canManage={
          projectPermissions?.isAdmin ||
          projectPermissions?.canEdit ||
          projectPermissions?.canManageParticipants
        }
        className="mt-5"
      />



      <div className="shadow sm:overflow-hidden sm:rounded-md mt-5">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div className="grid sm:grid-cols-8 gap-6">
            <div className="sm:col-span-8">
              <NotesDeluxeSection module={"project"} module_param={item?.id} />
            </div>
          </div>
        </div>
      </div>

      {/* Participants Section */}
      <ProjectAccessesSection projectId={item?.id} projectPermissions={projectPermissions} className="mt-5" />
    </>
  );
};

// Project Plan Preview Component
const ProjectPlanPreview = ({ projectId, className = "" }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get current week dates
        const today = new Date();
        const weekStart = startOfWeek(today, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(addWeeks(today, 2), { weekStartsOn: 1 }); // Show current week + 2 weeks ahead

        const response = await axios.get("/project_events", {
          params: {
            project_id: projectId,
            date_from: format(weekStart, "yyyy-MM-dd"),
            date_to: format(weekEnd, "yyyy-MM-dd"),
            per_page: 10, // Limit to 10 events for preview
          },
        });

        if (response.data && response.data.data) {
          setEvents(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching project events:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [projectId]);

  if (!projectId) {
    return null;
  }

  return (
    <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">{t("project.plan.preview.title")}</h3>
              <p className="text-sm text-gray-600">{t("project.plan.preview.subtitle")}</p>
            </div>
          </div>
          <Link
            to={`/dashboard/projects/${projectId}/plan`}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {t("project.plan.preview.go_to_full_plan")}
          </Link>
        </div>
      </div>
      <div className="bg-white px-6 py-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-500">{t("project.plan.preview.loading")}</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-600">{t("project.plan.preview.error")}</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">{t("project.plan.preview.no_events")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.slice(0, 5).map((event) => (
              <div key={event.id} className="flex items-start justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">{event.title || t("project.plan.preview.untitled_event")}</h4>
                  <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                    <span className="flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {event.start_date ? format(new Date(event.start_date), "dd.MM.yyyy") : "-"}
                      {event.end_date && event.end_date !== event.start_date && ` - ${format(new Date(event.end_date), "dd.MM.yyyy")}`}
                    </span>
                    {event.resources && event.resources.length > 0 && (
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {event.resources.length} {event.resources.length === 1 ? t("project.plan.preview.resource") : t("project.plan.preview.resources")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {events.length > 5 && (
              <div className="text-center pt-2">
                <Link
                  to={`/dashboard/projects/${projectId}/plan`}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {t("project.plan.preview.view_all", { count: events.length })}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
